import { create } from 'zustand';

/**
 * The merged-thread selection — the aggregate view's "one customer, many
 * shop numbers" chat panel (feat/merged-phone-thread).
 *
 *   - `key` — the thread grouping key, the SAME value Contract C's
 *     `group=phone` ordering groups by: `COALESCE(phone_e164, peer_id)`
 *     (see `groupKeyOf` in `features/conversations/group-runs.ts`). It is
 *     the path segment of `GET /api/v1/threads/{key}/messages` (URL-encoded
 *     at the API layer, raw here).
 *   - `memberIds` — the conversation ids (one per account) that make up the
 *     thread, as known by the inbox at selection time. The projector uses
 *     this to route `message.new` events into the open thread's cache and
 *     to suppress the unread bump; ThreadPanel uses it to resolve member
 *     conversation rows from the already-loaded conversations cache.
 */
export interface ThreadSelection {
  key: string;
  memberIds: string[];
}

interface UIState {
  selectedConversationId: string | null;
  selectedThread: ThreadSelection | null;
  selectedAccountId: string | null;
  setSelectedConversationId: (id: string | null) => void;
  setSelectedThread: (thread: ThreadSelection | null) => void;
  setSelectedAccountId: (id: string | null) => void;
}

/**
 * UI store — pure client-side selection state shared across the three columns.
 *
 *   - `selectedConversationId` — the single conversation row open in the chat
 *     panel.
 *   - `selectedThread` — the merged same-phone thread open in the chat panel
 *     (aggregate view). MUTUALLY EXCLUSIVE with `selectedConversationId`:
 *     each setter clears the other, so at most one of the two is non-null at
 *     any time and the chat panel's mode gate is a simple two-way branch.
 *   - `selectedAccountId` — the WhatsApp/Telegram/LINE account whose inbox
 *     the conversation-list column is currently filtering by. `null` means
 *     "所有账号" (all accounts) — the aggregate view across every account —
 *     rather than "nothing selected". This is also the initial value, so the
 *     console opens on the aggregate queue by default on sign-in.
 *
 * Server data (the conversation list itself, message history, account list)
 * lives in React Query — NOT here.
 */
export const useUIStore = create<UIState>((set) => ({
  selectedConversationId: null,
  selectedThread: null,
  selectedAccountId: null,
  // Mutual exclusivity is enforced HERE (not at every call site): selecting
  // a conversation closes any open thread and vice versa. Passing `null`
  // also clears the other — "close the panel" is one call either way.
  setSelectedConversationId: (id) =>
    set({ selectedConversationId: id, selectedThread: null }),
  setSelectedThread: (thread) =>
    set({ selectedThread: thread, selectedConversationId: null }),
  // Switching the inbox filter to a SPECIFIC account leaves the aggregate
  // view — merged rows don't exist there, so an open merged thread would be
  // an orphaned panel with no owning row; close it. Switching back to the
  // aggregate view (`null`) keeps whatever thread is open (the merged row
  // re-appears there). The single-conversation selection is deliberately
  // untouched — that has always survived account switches.
  setSelectedAccountId: (id) =>
    set((state) => ({
      selectedAccountId: id,
      selectedThread: id !== null ? null : state.selectedThread,
    })),
}));
